/**
 * Dark / light mode.
 *
 * The theme is applied by an inline script in header.php before first paint;
 * this module owns changes made after that, keeps the store in sync, and
 * mirrors the choice across open tabs.
 */

import { $$, on } from '@/lib/dom'
import { app, type Theme } from '@/store/app'

const STORAGE_KEY = 'atelier:theme'

function apply(theme: Theme): void {
  document.documentElement.dataset.theme = theme

  // Keep the browser UI (address bar, form controls) in step with the page.
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (meta) meta.content = theme === 'dark' ? '#0b0f19' : '#ffffff'

  $$('[data-theme-toggle]').forEach((button) => {
    button.setAttribute('aria-pressed', String(theme === 'dark'))
    button.setAttribute(
      'aria-label',
      theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode',
    )
  })
}

export function initTheme(): void {
  apply(app.get().theme)

  app.subscribe((state, previous) => {
    if (state.theme !== previous.theme) apply(state.theme)
  })

  $$('[data-theme-toggle]').forEach((button) => {
    on(button, 'click', () => {
      const next: Theme = app.get().theme === 'dark' ? 'light' : 'dark'
      app.set({ theme: next })
      try {
        localStorage.setItem(STORAGE_KEY, next)
      } catch {
        /* Storage blocked — the theme still applies for this session. */
      }
    })
  })

  // A change in one tab should be reflected in the others.
  on(window, 'storage', (event) => {
    const e = event as unknown as StorageEvent
    if (e.key === STORAGE_KEY && (e.newValue === 'dark' || e.newValue === 'light')) {
      app.set({ theme: e.newValue })
    }
  })
}
